import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger, useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  InlineMessage,
  Loader,
  ModalIsland,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import {
  getCertsColumns,
  performCertsListExport,
  resetCertListExportStatus,
  setCertsActionIsLoading,
} from '../../../../redux/certlist/action';
import { getMessageKey } from '../../../../utils/utils';

import CertsListSelectAndOrganizeYourData from './CertsListSelectAndOrganizeYourData';

import '../CertsListAction.css';

function CertsListExport({
  isOpen,
  setIsOpen,
  certKey,
  tileCampaignKey,
  campaignType,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const sectionRef = useRef(null);
  const { isScrolled: sectionRefIsScrolled } = useDetectScroll(
    null,
    sectionRef
  );

  const {
    lineAction: {
      certsListExport,
      columnPickerListError,
      certsActionIsLoading,
    } = {},
  } = useSelector((state) => state?.certifications?.list || {});

  const { messages } = useSelector((state) => state?.user);
  const isColumnPickerListLoading = certsListExport?.isLoading;
  const currentExportStatus =
    certsListExport?.certsListExportStatus?.exportStatus;

  useEffect(() => {
    if (isOpen) dispatch(getCertsColumns(certKey, tileCampaignKey));

    return () => {
      dispatch(resetCertListExportStatus());
    };
  }, [tileCampaignKey, certKey, isOpen]);

  const handleClose = () => {
    setIsOpen(false);

    dispatch(setCertsActionIsLoading(false));
  };

  const handleExportClick = async () => {
    try {
      const result = await dispatch(
        performCertsListExport(certKey, true, certsListExport.columnsToExport)
      );

      if (result.success) {
        handleClose();
      } else {
        Logger.error('Export API call failed');
      }
    } catch (error) {
      Logger.error('Export API call error:', error);
    }
  };

  const certsExportModalBody = () => {
    if (isColumnPickerListLoading) {
      return (
        <div className='CertListExportLoader'>
          <Loader kind='dots' format='inline' color='brand' />
        </div>
      );
    }

    if (columnPickerListError) {
      return (
        <div className='CertListExportLoader'>
          <InlineMessage
            text={
              messages.EXPORT_ALERT_FAILED_TITLE ||
              localize(intl, getMessageKey('CERTS_LIST_ACTION_API_FAILURE'))
            }
            colorTheme='critical'
            size='medium'
            isVisible
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        </div>
      );
    }

    const isExportAPIFailed = currentExportStatus === 'FAILED';

    return (
      <>
        <InlineMessage
          kind='background'
          text={
            messages['EXPORT_ALERT_FAILED_DESCRIPION.ATTESTATIONS'] ||
            localize(
              intl,
              getMessageKey('EXPORT_ALERT_FAILED_DESCRIPION.ATTESTATIONS')
            )
          }
          colorTheme='critical'
          size='medium'
          isVisible={isExportAPIFailed}
          leftIcon={<Icon kind='AlertCriticalFilled' />}
          className='CertsListExportErrorMessage'
        />
        <CertsListSelectAndOrganizeYourData
          certKey={certKey}
          campaignType={campaignType}
        />
      </>
    );
  };

  const certsExportFooterActionBar = () => (
    <Box className='CertsExportModalFooter'>
      <div className='CertsExportModalFooter-actionButtons'>
        <Button kind='outlined' size='medium' onClick={() => setIsOpen(false)}>
          {messages.CERTS_CANCEL ||
            localize(intl, getMessageKey('CERTS_CANCEL'))}
        </Button>
        <div className='CertsExportModalFooter-actionButtons--primary'>
          <Button
            kind='filled'
            size='medium'
            onClick={handleExportClick}
            isDisabled={columnPickerListError}
            isLoading={certsActionIsLoading}
            loaderText={
              messages.CERTS_LOADING ||
              localize(intl, getMessageKey('CERTS_LOADING'))
            }>
            {messages.CERTS_EXPORT ||
              localize(intl, getMessageKey('CERTS_EXPORT'))}
          </Button>
        </div>
      </div>
    </Box>
  );

  const title =
    messages.CERTS_EXPORT_TITLE ||
    localize(intl, getMessageKey('CERTS_EXPORT_TITLE'));
  const subtitle =
    messages.CERTS_EXPORT_SUBTITLE ||
    localize(intl, getMessageKey('CERTS_EXPORT_SUBTITLE'));

  return (
    <ModalIsland
      onClose={handleClose}
      isOpen={isOpen}
      className='CertsExportModal'
      title={title}
      subtitle={subtitle}
      headerIcon={<Icon kind='Export' size='large' />}
      contentBody={certsExportModalBody()}
      sectionRef={sectionRef}
      FooterActionBarComp={
        <FooterActionBar size='large' isScrolled={sectionRefIsScrolled}>
          {certsExportFooterActionBar()}
        </FooterActionBar>
      }
    />
  );
}

CertsListExport.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  certKey: PropTypes.number.isRequired,
  tileCampaignKey: PropTypes.number.isRequired,
};

export default CertsListExport;
